import { fleetMapping } from '@views/FleetMaster/types';
import { FLEET_CATEGORIES } from '../../constants';
import { TransportQuote } from './types';
import { isSameQuote } from './quoteIdentity';

export type ShipmentMethodBucket = 'pallet-network' | 'courier' | 'warehouse-lsp';

export interface ShipmentMethodBucketOption {
  id: ShipmentMethodBucket;
  title: string;
  description: string;
}

export const SHIPMENT_METHOD_BUCKETS: ShipmentMethodBucketOption[] = [
  {
    id: 'pallet-network',
    title: 'Pallet Network',
    description: 'Shared pallet networks — ambient, cross-carrier coverage.',
  },
  {
    id: 'courier',
    title: 'Courier',
    description: 'Dedicated vehicle, priced per mile.',
  },
  {
    id: 'warehouse-lsp',
    title: 'Warehouse LSP',
    description: 'Rates from warehouse and depot LSPs.',
  },
];

export const NOT_AVAILABLE_ON_LANE_COPY =
  'Not available on this lane or for these requirements';

export const COURIER_WEIGHT_MISSING_COPY =
  'Add the load weight to see courier prices';

export const COURIER_DESPATCH_NOTE_WEIGHT_MISSING_COPY =
  'Add weights to the SKUs on this despatch note to see courier prices';

export function bucketForSource(source: string): ShipmentMethodBucket {
  if (source === 'network') return 'pallet-network';
  if (source === 'mileage') return 'courier';
  return 'warehouse-lsp';
}

export function formatSourceLabel(source: string): string {
  switch (source) {
    case 'network':
      return 'Pallet Network';
    case 'mileage':
      return 'Courier';
    case 'postcode':
      return 'Postcode Rate';
    case 'daily':
      return 'Daily Rate';
    case 'multistop':
      return 'Multi-Stop';
    case 'additionalLoad':
      return 'Co-Loading';
    default:
      return source;
  }
}

export function formatPrice(price?: number, currency?: string): string {
  if (price == null) return 'TBC';
  const symbol =
    currency === 'EUR' ? '€' : currency === 'USD' ? '$' : '£';
  return `${symbol}${price.toFixed(2)}`;
}

export function formatVehicleLabel(vehicleType?: string): string {
  if (!vehicleType) return '';
  return fleetMapping[vehicleType] ?? vehicleType;
}

function roundToOneDecimalPlace(miles: number): number {
  return Number(miles.toFixed(1));
}

export function formatCourierDistance(
  distanceMiles?: number,
  chargedMiles?: number
): string {
  if (typeof distanceMiles !== 'number') return '';
  const shownDistanceMiles = roundToOneDecimalPlace(distanceMiles);
  const approximateDistance = `≈ ${shownDistanceMiles} miles`;
  if (typeof chargedMiles !== 'number') return approximateDistance;
  const shownChargedMiles = roundToOneDecimalPlace(chargedMiles);
  return shownChargedMiles > shownDistanceMiles
    ? `${approximateDistance} (minimum ${shownChargedMiles} miles charged)`
    : approximateDistance;
}

function isPricedQuote(quote: TransportQuote): boolean {
  return typeof quote.price === 'number' && quote.price > 0;
}

function quotesForBucket(
  quotes: TransportQuote[],
  bucketId: ShipmentMethodBucket
): TransportQuote[] {
  return quotes.filter((quote) => bucketForSource(quote.source) === bucketId);
}

export function isCourierWeightMissing(
  totalWeight?: number | string | null
): boolean {
  const numericTotalWeight = Number(totalWeight);
  return !Number.isFinite(numericTotalWeight) || numericTotalWeight <= 0;
}

export interface LoadSize {
  totalWeightKg: number | null;
  totalPallets: number | null;
}

export const COURIER_MAXIMUM_LOAD_KG = 3500;

export const COURIER_MAXIMUM_PALLETS = 6;

function isOverLimit(amount: number | null, limit: number | null): boolean {
  return amount !== null && limit !== null && amount > limit;
}

export function canCourierTakeLoad(load: LoadSize): boolean {
  return (
    !isOverLimit(load.totalWeightKg, COURIER_MAXIMUM_LOAD_KG) &&
    !isOverLimit(load.totalPallets, COURIER_MAXIMUM_PALLETS)
  );
}

export function withoutCourierQuotesWhenCourierCannotTakeLoad(
  quotes: TransportQuote[],
  load: LoadSize
): TransportQuote[] {
  if (canCourierTakeLoad(load)) return quotes;
  return quotes.filter((quote) => bucketForSource(quote.source) !== 'courier');
}

export function shipmentMethodBucketsForLoad(
  load: LoadSize
): ShipmentMethodBucketOption[] {
  if (canCourierTakeLoad(load)) return SHIPMENT_METHOD_BUCKETS;
  return SHIPMENT_METHOD_BUCKETS.filter((bucket) => bucket.id !== 'courier');
}

export function canVehicleCarryLoad(
  vehicleType: string,
  load: LoadSize
): boolean {
  const vehicle = FLEET_CATEGORIES.find(
    (category) => category.value === vehicleType
  );
  if (!vehicle) return true;
  return (
    !isOverLimit(load.totalWeightKg, vehicle.weightLimitKg) &&
    !isOverLimit(load.totalPallets, vehicle.palletLimit)
  );
}

export function bucketsWithAnyQuote(
  quotes: TransportQuote[]
): ShipmentMethodBucket[] {
  return SHIPMENT_METHOD_BUCKETS.map((bucket) => bucket.id).filter(
    (bucketId) => quotesForBucket(quotes, bucketId).length > 0
  );
}

export function summariseBucket(bucketQuotes: TransportQuote[]): string | null {
  if (bucketQuotes.length === 0) return null;
  const optionsSuffix = bucketQuotes.length === 1 ? 'option' : 'options';
  const pricedQuotes = bucketQuotes.filter(isPricedQuote);
  if (pricedQuotes.length === 0) {
    return `Price to be confirmed • ${bucketQuotes.length} ${optionsSuffix}`;
  }
  const cheapestPricedQuote = pricedQuotes.reduce(
    (cheapest, quote) => (quote.price! < cheapest.price! ? quote : cheapest),
    pricedQuotes[0]
  );
  const formattedCheapestPrice = formatPrice(
    cheapestPricedQuote.price,
    cheapestPricedQuote.currency || 'GBP'
  );
  return `From ${formattedCheapestPrice} • ${bucketQuotes.length} ${optionsSuffix}`;
}

export interface ShipmentMethodBucketDisplay {
  summary: string;
  isSelectable: boolean;
}

export function describeShipmentMethodBucket(
  bucketId: ShipmentMethodBucket,
  quotes: TransportQuote[],
  totalWeight?: number | string | null,
  isFromDespatchNote = false
): ShipmentMethodBucketDisplay {
  const bucketQuotes = quotesForBucket(quotes, bucketId);
  const isCourierWaitingForWeight =
    bucketId === 'courier' &&
    bucketQuotes.length === 0 &&
    isCourierWeightMissing(totalWeight);
  if (isCourierWaitingForWeight) {
    return {
      summary: isFromDespatchNote
        ? COURIER_DESPATCH_NOTE_WEIGHT_MISSING_COPY
        : COURIER_WEIGHT_MISSING_COPY,
      isSelectable: false,
    };
  }
  return {
    summary: summariseBucket(bucketQuotes) ?? NOT_AVAILABLE_ON_LANE_COPY,
    isSelectable: bucketQuotes.length > 0,
  };
}

function isSameServiceLevel(
  serviceName: string | undefined,
  serviceLevel: string
): boolean {
  return (serviceName ?? '').toLowerCase() === serviceLevel.toLowerCase();
}

export function filterQuotesByServiceLevel(
  quotes: TransportQuote[],
  bucketId: ShipmentMethodBucket,
  serviceLevel: string
): TransportQuote[] {
  const bucketQuotes = quotesForBucket(quotes, bucketId);
  if (bucketId !== 'pallet-network' || !serviceLevel) return bucketQuotes;
  return bucketQuotes.filter((quote) =>
    isSameServiceLevel(quote.serviceName, serviceLevel)
  );
}

export function nextShipmentMethod(
  quotes: TransportQuote[],
  currentValue: string
): string {
  const bucketsOnOffer = bucketsWithAnyQuote(quotes);
  if (bucketsOnOffer.includes(currentValue as ShipmentMethodBucket)) {
    return currentValue;
  }
  return bucketsOnOffer.length === 1 ? bucketsOnOffer[0] : '';
}

export function isShipmentMethodSelectionRequired(
  quotes: TransportQuote[],
  isLoadingQuotes: boolean,
  quotesError: string | null
): boolean {
  return (
    !quotesError && (isLoadingQuotes || bucketsWithAnyQuote(quotes).length > 0)
  );
}

export function isQuoteSelectionRequiredForChosenBucket(
  quotes: TransportQuote[],
  chosenBucket: string,
  serviceLevelFilter: string,
  isLoadingQuotes: boolean,
  quotesError: string | null
): boolean {
  const chosenBucketQuotes = chosenBucket
    ? filterQuotesByServiceLevel(
        quotes,
        chosenBucket as ShipmentMethodBucket,
        serviceLevelFilter
      )
    : [];
  const anyQuoteOffered = chosenBucket
    ? chosenBucketQuotes.length > 0
    : bucketsWithAnyQuote(quotes).length > 0;
  return !quotesError && (isLoadingQuotes || anyQuoteOffered);
}

export function isSelectedQuoteVisibleAfterFilterChange(
  quotes: TransportQuote[],
  chosenBucket: string,
  serviceLevelFilter: string,
  selectedRateCardId: string,
  selectedServiceName: string,
  selectedVehicleType: string
): boolean {
  if (!selectedRateCardId) return true;
  const visibleQuotes = filterQuotesByServiceLevel(
    quotes,
    chosenBucket as ShipmentMethodBucket,
    serviceLevelFilter
  );
  return visibleQuotes.some((quote) =>
    isSameQuote(
      quote,
      selectedRateCardId,
      selectedServiceName,
      selectedVehicleType
    )
  );
}
